/**
 * Profile route — the Omat tiedot member surface with a hub + in-page section
 * views, plus a separate admin view.
 *
 * IA (026-proto-convergence-v2 US3, lead-decided): a single /profile route. The
 * member landing is a hub (identity + membership status + three entry points:
 * Omat yhteystiedot, Jäsenmaksu, Jäsentodistus). Selecting an entry point swaps
 * the in-page view to that section with a back affordance returning to the hub.
 * No new routes; the active view is tracked with useState here. The one way in
 * other than the hub is the overdue-fee notice on Home (057 R6): its link
 * carries `{ profileView: 'fee' }` as router state, which seeds the initial
 * view and is then replaced with null state on the same address, so the fee
 * view never gets a URL of its own (D-PD-010) and a reload opens the hub.
 *
 * Boundary wiring (mirrors the original memberProfile pattern):
 * - The hub + contact views read the memberProfile port -> wrapped in
 *   PortErrorBoundary(memberProfile) -> Suspense(ProfileSkeleton).
 * - The fee view reads membershipBilling; the certificate view reads
 *   membershipCertificate -> each wrapped in its own PortErrorBoundary ->
 *   Suspense, so a fee-port failure cannot blank the certificate view and vice
 *   versa.
 *
 * Invariants kept from 018-omat-tiedot:
 * - The <h2> "Omat tiedot" heading.
 * - The admin view (name + avatar only) does NOT call any member port.
 *
 * Item-4 r1 (owner live-review) changed two former invariants:
 * - The per-page demo-data badge is REMOVED (C1): P7 is capability honesty, not
 *   demo-build disclosure. The fee view keeps payDeferred. The certificate view
 *   no longer carries a demo disclosure (049/D-215): it renders the server's
 *   `member.certificate.preview`, and an FR-008 block degrades it to
 *   `profile.certificate.unavailable`.
 * - The contact view is read-only by DEFAULT but enters inline edit on "Päivitä
 *   tietoja" (C4), so it renders <input>s in edit mode. The hub, fee, and
 *   certificate views have no free-text fields. The fee view is not read-only
 *   since 048: its e-invoice proposal request is a bank <select> plus a submit
 *   that performs an audited write.
 *
 * Sub-components live in `routes/profile/` to keep this file under 400 lines
 * (FR-016 / file-size-400).
 *
 * Owned by feature 026-proto-convergence-v2 (extends 018-omat-tiedot T017).
 * Task: T030
 */

import { type JSX, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AnalyticsRouteReady } from '../analytics/AnalyticsCaptureProvider.js';
import { AnalyticsConsentProfileControl } from '../analytics/AnalyticsConsentProfileControl.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { useMemberProfile } from '../member-profile/useMemberProfile.js';
import { isPermanentCertificateBlock } from '../membership-certificate/certificateBlockReason.js';
import { useSession } from '../session/useSession.js';
import { deriveAvatarInitials } from '../shell/avatarInitials.js';
import { PortErrorBoundary } from '../shell/PortErrorBoundary.js';
import { ContactSection } from './profile/ContactSection.js';
import { MemberProfileHub } from './profile/MemberProfileHub.js';
import { MembershipCertificateSection } from './profile/MembershipCertificateSection.js';
import { MembershipFeeSection } from './profile/MembershipFeeSection.js';
import { MyDataSection } from './profile/MyDataSection.js';
import { ProfileBackButton } from './profile/ProfileSectionHeader.js';
import { ProfileSkeleton } from './profile/ProfileSkeleton.js';
import { ProfileUnavailable } from './profile/ProfileUnavailable.js';
import { isFeeViewHandoff } from './profile/profileNavigationState.js';
import { SectionUnavailable } from './profile/SectionUnavailable.js';

// pb-6 only: the shell `<main>` already pads the top (pt-top-bar + the content
// wrapper's py-4); a second top pad pushed the heading/back too far below the top
// bar (item-4 r1 C2). The proto sits the heading close to the top bar.
// flex-1 so the member hub's full-bleed sheet can stretch to the bottom nav (the
// shell's main + content column are flex-col/flex-1). Sub-views keep top-aligned
// content; the filled height is invisible when nothing opts to stretch.
const pageClasses = 'flex flex-1 flex-col gap-4 px-4 pb-6';
// No vertical margins: the heading shares the C7 flex-ROW header bar with the
// back control (items-center keeps "← Omat tiedot" aligned), and the section's
// gap-4 spaces the header from the content below.
const headingClasses = 'text-2xl font-bold text-foreground';
const adminAvatarClasses =
  'flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold';

/** Which in-page view the member surface is showing. */
type ProfileView = 'hub' | 'contact' | 'fee' | 'certificate' | 'myData' | 'analyticsConsent';

// Hub + contact views read the memberProfile port; they share one boundary.
function MemberProfileLanding({
  view,
  onSelectContact,
  onSelectFee,
  onSelectCertificate,
  onSelectMyData,
  onSelectAnalyticsConsent,
}: {
  readonly view: 'hub' | 'contact';
  readonly onSelectContact: () => void;
  readonly onSelectFee: () => void;
  readonly onSelectCertificate: () => void;
  readonly onSelectMyData: () => void;
  readonly onSelectAnalyticsConsent: () => void;
}): JSX.Element | null {
  const session = useSession();
  const profile = useMemberProfile();
  const authState = session.state;

  if (authState.kind !== 'member') {
    return null;
  }

  // The per-page "demo data" badge is removed (item-4 r1 C1): P7 is capability
  // honesty, not demo-build disclosure. The contact view's back control is now
  // lifted to the route level (C5).
  if (view === 'contact') {
    return (
      <>
        <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'profile' }} />
        <ContactSection profile={profile} />
      </>
    );
  }

  return (
    <>
      <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'profile' }} />
      <MemberProfileHub
        displayName={authState.displayName}
        profile={profile}
        onSelectContact={onSelectContact}
        onSelectFee={onSelectFee}
        onSelectCertificate={onSelectCertificate}
        onSelectMyData={onSelectMyData}
        onSelectAnalyticsConsent={onSelectAnalyticsConsent}
      />
    </>
  );
}

// Member-session surface: renders the active in-page view + per-port boundaries.
// The view state + the back affordance are owned by the route (Profile); this
// component is driven by `view`/`setView` so the route can render the single
// lifted back control above the heading (item-4 r1 C5).
function MemberProfileSurface({
  view,
  setView,
}: {
  readonly view: ProfileView;
  readonly setView: (view: ProfileView) => void;
}): JSX.Element {
  const t = useTranslate();

  if (view === 'analyticsConsent') {
    return <AnalyticsConsentProfileControl />;
  }

  if (view === 'fee') {
    return (
      <PortErrorBoundary
        // Distinct key per view so switching views (e.g. back to the hub from an
        // errored section) remounts a fresh boundary rather than reusing this
        // instance with stale caught-error state, which would strand the member.
        key="fee"
        portName="membershipBilling"
        fallback={
          <SectionUnavailable
            title={t('profile.fee.heading')}
            message={t('profile.fee.unavailable')}
          />
        }
      >
        <Suspense fallback={<ProfileSkeleton />}>
          <MembershipFeeSection />
        </Suspense>
      </PortErrorBoundary>
    );
  }

  if (view === 'certificate') {
    return (
      <PortErrorBoundary
        key="certificate"
        portName="membershipCertificate"
        // Owner decision 2026-09-24: a block the member can wait out reads
        // "try again later"; one only member services can clear says so
        // instead. Since O-021 the preview and the document are one
        // derivation, so an FR-008 block lands HERE (the whole section
        // degrades) rather than on the download button — which is why the
        // distinction has to be made at the boundary.
        fallback={(error) => (
          <SectionUnavailable
            title={t('profile.certificate.heading')}
            message={
              isPermanentCertificateBlock(error)
                ? t('profile.certificate.contactService')
                : t('profile.certificate.unavailable')
            }
          />
        )}
      >
        <Suspense fallback={<ProfileSkeleton />}>
          <MembershipCertificateSection />
        </Suspense>
      </PortErrorBoundary>
    );
  }

  if (view === 'myData') {
    return (
      <PortErrorBoundary
        key="myData"
        portName="myData"
        fallback={
          <SectionUnavailable
            title={t('profile.hub.myData')}
            message={t('profile.myData.unavailable')}
          />
        }
      >
        <Suspense fallback={<ProfileSkeleton />}>
          <MyDataSection />
        </Suspense>
      </PortErrorBoundary>
    );
  }

  // hub | contact — both read the memberProfile port.
  return (
    <PortErrorBoundary
      key="member-profile"
      portName="memberProfile"
      fallback={<ProfileUnavailable />}
    >
      <Suspense fallback={<ProfileSkeleton />}>
        <MemberProfileLanding
          view={view}
          onSelectContact={() => setView('contact')}
          onSelectFee={() => setView('fee')}
          onSelectCertificate={() => setView('certificate')}
          onSelectMyData={() => setView('myData')}
          onSelectAnalyticsConsent={() => setView('analyticsConsent')}
        />
      </Suspense>
    </PortErrorBoundary>
  );
}

// Admin-session view: name + avatar only (no port call).
function AdminProfileView(): JSX.Element | null {
  const t = useTranslate();
  const session = useSession();
  const authState = session.state;

  if (authState.kind !== 'admin') {
    return null;
  }

  const displayName = authState.displayName;
  const initials = deriveAvatarInitials(displayName);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className={adminAvatarClasses}>{initials}</div>
        <div>
          <div className="text-lg font-semibold text-foreground">{displayName}</div>
          <div className="text-sm text-muted-foreground">{t('profile.admin.heading')}</div>
        </div>
      </div>
    </div>
  );
}

export function Profile(): JSX.Element {
  const t = useTranslate();
  const session = useSession();
  const authState = session.state;

  const isMember = authState.kind === 'member';
  const isAdmin = authState.kind === 'admin';

  // The active member sub-view. The back affordance is lifted to the route level
  // (item-4 r1 C5) and sits INLINE on the heading row (item-4 r1.1 C7): a single
  // "Takaisin" control on the SAME flex-ROW as the route <h2> ("← Omat tiedot",
  // the proto's per-page header bar), preceding the heading. The hub is the top
  // of the surface and shows none. Keeping the back outside the per-section error
  // boundaries also keeps it working when a section degrades to its fallback.
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<ProfileView>(() =>
    isFeeViewHandoff(location.state) ? 'fee' : 'hub',
  );
  // Consume the handoff once: the view already holds it, and leaving it in the
  // history entry would reopen the fee view on back/forward.
  const handoffPending =
    typeof location.state === 'object' &&
    location.state !== null &&
    Object.hasOwn(location.state, 'profileView');
  useEffect(() => {
    if (handoffPending) {
      void navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
    }
  }, [handoffPending, navigate, location.pathname, location.search, location.hash]);
  const backToHub = (): void => setView('hub');
  const inMemberSubView = isMember && view !== 'hub';

  return (
    <section className={pageClasses} aria-labelledby="profile-title">
      <div className="flex items-center gap-3">
        {inMemberSubView && <ProfileBackButton onBack={backToHub} />}
        <h2 id="profile-title" className={headingClasses}>
          {t('profile.title')}
        </h2>
      </div>

      {isMember && <MemberProfileSurface view={view} setView={setView} />}

      {isAdmin && <AdminProfileView />}
    </section>
  );
}
